import { useSelector } from 'react-redux';
import { store } from '../data/redux/store';
import { isModuleEnabledIn, type FeatureModuleCode } from './featureModules';

const normalize = (val: any): string => {
  if (!val || typeof val !== "string") return "";
  return val.trim().toLowerCase().replace(/\s+/g, "");
};

// ── Permissions V2 — KEY-FIRST gating ─────────────────────────────────────────
// `checkModulePermission` now decides from the granted key list. When the login
// carries `permissionKeys` (V2), rights are ignored — so removing a grant in the
// admin immediately hides the module/action after the user's keys refresh (re-login
// or a permission-refresh push). Legacy `rights` are used ONLY as a safety net for
// tokens that carry no keys at all (pre-V2), so nobody is bricked mid-rollout.
//
// Two map kinds:
//  - EXACT_MODULE_KEY: modules that resolve to a single key (fake/sub-module gates).
//  - MODULE_KEY_PREFIX: normal modules; the flag picks the verb (create/edit/delete),
//    falling back to `.manage` for manage-style modules, and canAccess = `<prefix>.view`
//    (the module is visible only to a role that can actually view it).
const EXACT_MODULE_KEY: Record<string, string> = {
  accountactivation: 'employees.activation',
  impersonateusers: 'employees.impersonate',
  employeebankdetails: 'employees.bank.view',
  employeedocuments: 'employees.documents.view',
  checkinoutsettings: 'attendance.settings',
  mytrainings: 'training.take',
  mytraining: 'training.take',
  didnotturnup: 'jobs.dnu',
  changeholidaystatus: 'holidays.approve',
  startyourdayattendance: 'attendance.startday',
  replacevehicle: 'vehicles.replace',
  // Scoring config is its own admin-category key, not a verb under the report's
  // prefix, so it resolves exactly rather than through MODULE_KEY_PREFIX.
  drivertripreportsettings: 'drivertripreport.settings',
  // The report menu entry must require .view specifically. Through MODULE_KEY_PREFIX
  // its canAccess was "any key under drivertripreport.", so holding only .settings or
  // only .generate revealed the report page and its driver data.
  drivertripreport: 'drivertripreport.view',
  kanbanview: 'kanban.view',
  kanbanimport: 'kanban.import',
  trainingdashboard: 'training.dashboard.view',
  auditdocrequest: 'training.records.request',
  trainingrecordrequest: 'training.records.request',
  trainingrecordapproval: 'training.records.approve',
  // "Induction Templates" sidebar row (sidebarData.tsx) — split out from the
  // "Employee Induction" module below so it needs its own key, not just any
  // induction.* grant.
  inductiontemplates: 'induction.templates.manage',
};

const MODULE_KEY_PREFIX: Record<string, string> = {
  employees: 'employees',
  'employee-types': 'employeetypes',
  contracttype: 'contracttypes',
  departments: 'departments',
  area: 'areas',
  volunteer: 'volunteers',
  shifttype: 'shifttypes',
  sitetype: 'sitetypes',
  leavetypes: 'leavetypes',
  holidayrequest: 'holidays',
  clothingitem: 'clothing.items',
  clothingrequest: 'clothing.requests',
  clothingreturn: 'clothing.returns',
  machineassignments: 'machine.assignments',
  machines: 'machine',
  allreports: 'reports',
  submissionreports: 'reportsubmissions',
  vanphoto: 'vanphoto',
  sanitizedreports: 'sanitizedreports',
  incidentreport: 'incidents',
  audittype: 'audittypes',
  auditreport: 'auditreports',
  auditreportsubmissions: 'auditsubmissions',
  audit: 'audits',
  customerrequestreport: 'customerrequests',
  nonconformancesreport: 'ncr',
  nonconformancesreportsubmissions: 'ncrsubmissions',
  importantdocs: 'importantdocs',
  trackinglist: 'vehicles.tracking',
  certifications: 'certifications',
  certificatetypes: 'certifications.types',
  sop: 'sop',
  trainings: 'training.catalog',
  trainingcategories: 'training.categories',   // Fixed: was incorrectly 'training.catalog'
  trainingassignments: 'training.assignments',
  'auditingclockin/outtimes': 'auditorclock',
  notificationtype: 'notifications.types',
  notificationtemplate: 'notifications.templates',
  adminnotificationpermissions: 'notifications.templatepermissions',
  systemsetting: 'system.settings',
  attendance: 'attendance',
  customers: 'customers',
  customerpermissions: 'customers.permissions',
  farms: 'farms',
  teams: 'teams',
  vehicles: 'vehicles',
  roles: 'roles',
  employeecompanies: 'employeecompanies',
  // Sidebar/route name "Employee Induction" — the induction records routes
  // (list/pending/detail). Access is scoped to induction.records.* via
  // MODULE_ACCESS_KEYS below, not the full induction.* prefix — the template
  // settings routes now live under the separate "Induction Templates" module
  // (see inductiontemplates in EXACT_MODULE_KEY) so induction.templates.manage
  // doesn't also unlock this one, and vice versa.
  employeeinduction: 'induction',
  // Sidebar name "Custom Email" -> customemail.view (menu visibility) / customemail.send.
  customemail: 'customemail',
  // Sidebar name "Custom WhatsApp" -> customwhatsapp.view (menu visibility) / customwhatsapp.send.
  customwhatsapp: 'customwhatsapp',
};

// Modules whose menu visibility is NOT "<prefix>.view".
//
// canAccess normally requires the module's own `.view` key (see the switch below), so a
// role holding only a mutate/admin key no longer sees the menu entry. These modules have
// no `.view` key in the catalog at all — they are manage-only screens, or their view key
// lives under a deeper namespace — so they list the keys that should reveal them instead.
// Without an entry here they would be invisible to everyone.
const MODULE_ACCESS_KEYS: Record<string, string[]> = {
  machines: ['machine.view', 'machine.manage'],
  machineassignments: ['machine.assignments.view', 'machine.assignments.manage'],
  // Manage-only screens: the catalog defines a `.manage` key and no `.view`.
  customerpermissions: ['customers.permissions.manage'],
  trainingcategories: ['training.categories.manage'],
  notificationtype: ['notifications.types.manage'],
  notificationtemplate: ['notifications.templates.manage'],
  adminnotificationpermissions: ['notifications.templatepermissions.manage'],
  // Submission Reports has no view key in the catalog — only .submit and .manage.
  submissionreports: ['reportsubmissions.submit', 'reportsubmissions.manage'],
  // Induction's view key is namespaced under .records, and the menu also covers the
  // templates screen, so either key reveals the section.
  employeeinduction: ['induction.records.view', 'induction.templates.manage']
};

export const checkModulePermission = (moduleName: string, permissionType: 'canAccess' | 'canAdd' | 'canEdit' | 'canDelete'): boolean => {
  const state = store.getState();
  const keys = state.permissions?.permissionKeys;
  const norm = normalize(moduleName);

  // KEY-FIRST: whenever the session carries V2 keys, decisions come from them.
  if (Array.isArray(keys) && keys.length > 0) {
    const exact = EXACT_MODULE_KEY[norm];
    if (exact) return hasPermission(exact);

    const prefix = MODULE_KEY_PREFIX[norm] || norm;
    switch (permissionType) {
      case 'canAccess':
        if (MODULE_ACCESS_KEYS[norm]) {
          return MODULE_ACCESS_KEYS[norm].some(key => hasPermission(key));
        }
        // Menu/route visibility requires the module's own view key. This used to be
        // hasAnyKey(`${prefix}.`) — "holds anything in this module" — which revealed a
        // screen to anyone holding only a mutate or admin key (e.g. drivertripreport.settings
        // alone opened the report and its data). Modules with no `.view` key are listed in
        // MODULE_ACCESS_KEYS above.
        return hasPermission(`${prefix}.view`);
      case 'canAdd':
        // "add" spans create-like verbs: create, submit (submissions), request (docs),
        // generate (reports produced on demand rather than authored).
        return hasPermission(`${prefix}.create`) || hasPermission(`${prefix}.submit`) ||
          hasPermission(`${prefix}.request`) || hasPermission(`${prefix}.manage`) ||
          hasPermission(`${prefix}.generate`);
      case 'canEdit':
        return hasPermission(`${prefix}.edit`) || hasPermission(`${prefix}.manage`);
      case 'canDelete':
        return hasPermission(`${prefix}.delete`) || hasPermission(`${prefix}.manage`);
      default:
        return false;
    }
  }

  // Permissions V2 is now the ONLY source of authority. No keys => no access.
  // (The legacy `rights` fallback was removed at cutover.)
  return false;
};

export const hasAnyPermission = (moduleName: string, permissions: ('canAccess' | 'canAdd' | 'canEdit' | 'canDelete')[]): boolean => {
  if (!permissions.length) return false;
  return permissions.some(permission => checkModulePermission(moduleName, permission));
};

// ─── Permissions V2 (key-based) ──────────────────────────────────────────────
// Backed by `permissionKeys` (delivered alongside legacy rights during migration).
// Each entry is a dotted key with an optional scope suffix, e.g. "employees.view@team".

export type PermissionScope = 'own' | 'team' | 'all';

const parseKeyEntry = (entry: string): { key: string; scope: PermissionScope } => {
  const at = entry.indexOf('@');
  if (at === -1) return { key: entry.trim().toLowerCase(), scope: 'all' };
  return {
    key: entry.slice(0, at).trim().toLowerCase(),
    scope: (entry.slice(at + 1).trim().toLowerCase() as PermissionScope) || 'all',
  };
};

const getPermissionKeys = (): string[] => {
  const state = store.getState();
  const keys = state.permissions?.permissionKeys;
  return Array.isArray(keys) ? keys : [];
};

// True if the caller holds the exact permission key (scope-agnostic).
export const hasPermission = (key: string): boolean => {
  const target = key.trim().toLowerCase();
  return getPermissionKeys().some(entry => parseKeyEntry(entry).key === target);
};

// True if the caller holds ANY key under a prefix — use for module/section visibility,
// e.g. hasAnyKey('employees.') to decide whether to show the Employees menu.
export const hasAnyKey = (prefix: string): boolean => {
  const p = prefix.trim().toLowerCase();
  return getPermissionKeys().some(entry => parseKeyEntry(entry).key.startsWith(p));
};

// The granted record scope for a key, or null if the key is not granted.
export const scopeOf = (key: string): PermissionScope | null => {
  const target = key.trim().toLowerCase();
  const found = getPermissionKeys().find(entry => parseKeyEntry(entry).key === target);
  return found ? parseKeyEntry(found).scope : null;
};

// ─── Company product modules ─────────────────────────────────────────────────
// A platform admin switches modules (backend FeatureModuleCatalog codes) on or off
// per company. Screens and menu items gated by permission keys already disappear,
// because the login drops a switched-off module's keys. Use these for UI that isn't
// driven by a key (a tab inside the employee form, a dashboard widget).

export type { FeatureModuleCode };

// Non-React call sites.
export const isFeatureModuleEnabled = (code: FeatureModuleCode): boolean =>
  isModuleEnabledIn(store.getState().permissions?.enabledModules, code);

// Components: re-renders when the company's modules change.
export const useFeatureModule = (code: FeatureModuleCode): boolean =>
  useSelector((state: any) => isModuleEnabledIn(state.permissions?.enabledModules, code));
